/**
 * Server-quote-driven price rows for the vehicle detail page.
 * All numbers come from POST /api/bookings/quote (single source of truth
 * — no client-side pricing math).
 *
 * VAT-inclusive: `total` IS the customer-payable price. VAT is shown as
 * an informational line (never added on top).
 */
import { isoToEU } from "../../lib/format";

export default function PriceSummary({
  quote, quoteLoading, form, voucherPreview, dailyRate, taxRatePct,
  securityDeposit, depositPct,
  availability, leadDays, minPickup,
}) {
  if (!quote) {
    return (
      <div className="py-6 text-center text-xs uppercase tracking-wider text-neutral-500" data-testid="quote-loading">
        {quoteLoading ? "Calculating…" : "Select dates to see pricing"}
      </div>
    );
  }

  const days = quote.days;
  const subtotal = quote.subtotal;
  const addonsTotal = quote.addons_total;
  const pickupFee = quote.pickup_fee;
  const returnFee = quote.return_fee;
  const outOfHoursFee = quote.out_of_hours_fee;
  const voucherDiscount = quote.voucher_discount ?? Number(voucherPreview?.discount || 0);
  const voucherMeta = quote.voucher_meta;
  const taxes = quote.taxes;
  const total = quote.total;
  const netTotal = quote.net_total;
  const advanceDeposit = quote.advance_deposit ?? 0;
  const balanceDue = quote.balance_due_at_pickup ?? 0;
  const advancePct = quote.advance_deposit_pct ?? 0;
  const vatRate = quote.vat_rate_pct ?? taxRatePct ?? 24;

  // Original (pre-discount) prices for strikethrough visual.
  const originalTotal = total + voucherDiscount;

  return (
    <div data-testid="price-summary">
      <Row label={`${dailyRate.toFixed(2)} × ${days} days`} value={`${subtotal.toFixed(2)}`} />
      {addonsTotal > 0 && <Row label="Add-ons" value={`${addonsTotal.toFixed(2)}`} />}
      {pickupFee > 0 && (
        <Row label={`Pickup fee · ${form.pickup_location}`} value={`${pickupFee.toFixed(2)}`} />
      )}
      {returnFee > 0 && (
        <Row label={`Return fee · ${form.return_location}`} value={`${returnFee.toFixed(2)}`} />
      )}
      {outOfHoursFee > 0 && (
        <Row label="Out-of-hours fee" value={`${outOfHoursFee.toFixed(2)}`} />
      )}
      {voucherDiscount > 0 && voucherMeta && (
        <div className="my-2 p-2 rounded bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800" data-testid="voucher-applied">
          <div className="flex justify-between items-center text-xs">
            <span className="text-emerald-800 dark:text-emerald-300">
              {voucherMeta.is_automatic ? "Promo applied · " : "Code · "}
              <strong>{voucherMeta.code}</strong>
              {voucherMeta.description && <span className="text-neutral-600 dark:text-neutral-400"> — {voucherMeta.description}</span>}
            </span>
            <span className="text-emerald-700 dark:text-emerald-400 tabular-nums font-medium">−€{voucherDiscount.toFixed(2)}</span>
          </div>
        </div>
      )}
      <div className="divider-line my-3"></div>

      {/* VAT-inclusive: informational only, no line added to total */}
      <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400 mb-1" data-testid="vat-info">
        <span>Includes {vatRate}% VAT</span>
        <span className="tabular-nums">€{taxes.toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-xs text-neutral-400 dark:text-neutral-500 mb-3">
        <span>Net (excl. VAT)</span>
        <span className="tabular-nums">€{netTotal.toFixed(2)}</span>
      </div>

      {voucherDiscount > 0 ? (
        <div className="flex justify-between items-baseline mb-1" data-testid="total-with-discount">
          <span className="font-display text-xl">Total</span>
          <div className="text-right">
            <span className="text-sm text-neutral-400 line-through mr-2 tabular-nums" data-testid="original-total">
              €{originalTotal.toFixed(2)}
            </span>
            <span className="font-display text-2xl text-emerald-700 dark:text-emerald-400 tabular-nums" data-testid="discounted-total">
              €{total.toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <Row label="Total" value={`${total.toFixed(2)}`} bold />
      )}

      {/* Advance deposit + balance breakdown */}
      {advancePct > 0 && (
        <div className="mt-4 p-3 border border-black/10 dark:border-white/10 bg-[#EFECE6]/60 dark:bg-neutral-800/40 rounded space-y-1" data-testid="advance-deposit-block">
          <div className="flex justify-between text-sm" data-testid="advance-payment-line-live">
            <span className="text-neutral-700 dark:text-neutral-300">
              Advance Payment ({advancePct.toFixed(0)}%)
            </span>
            <span className="tabular-nums font-medium">€{advanceDeposit.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm" data-testid="remaining-balance-line-live">
            <span className="text-neutral-700 dark:text-neutral-300">Remaining Balance (Due at Pick-up)</span>
            <span className="tabular-nums font-medium">€{balanceDue.toFixed(2)}</span>
          </div>
        </div>
      )}

      {securityDeposit > 0 && (
        <div className="mt-3 p-3 border border-black/10 bg-[#EFECE6]/60 rounded" data-testid="deposit-preview">
          <div className="flex justify-between text-xs">
            <span className="text-neutral-600">
              {(quote.security_deposit_amount ?? 0) > 0
                ? "Refundable security deposit (held at pickup)"
                : `Refundable security deposit (${depositPct.toFixed(0)}%, held at pickup)`}
            </span>
            <span className="tabular-nums font-medium">€{securityDeposit.toFixed(2)}</span>
          </div>
        </div>
      )}

      {leadDays > 0 && (
        <p className="text-[11px] text-neutral-500 mt-3" data-testid="lead-time-notice">
          Bookings require {leadDays} day{leadDays === 1 ? "" : "s"} lead time. Earliest pickup: {isoToEU(minPickup) || minPickup}.
        </p>
      )}

      {!availability.available && (
        <div className="mt-4 p-3 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="availability-warning">
          {availability.reason || "Not available for these dates."}
          {availability.conflict && (
            <p className="text-xs mt-1 text-red-600">
              Booked from {isoToEU(availability.conflict.pickup) || availability.conflict.pickup} to {isoToEU(availability.conflict.return) || availability.conflict.return}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold, discount }) {
  const tone = discount ? "text-emerald-700" : "text-neutral-700";
  return (
    <div className={`flex justify-between text-sm ${bold ? "font-display text-xl" : tone} mb-1`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
